import React from 'react'
import { Box } from '@mui/material'
import clsx from 'clsx'
import TablePagination from '@mui/material/TablePagination'
import { useIntl } from 'react-intl'
import MediaLibraryCard from './MediaLibraryCard'
import { useMediaLibrary } from './MediaLibraryContext'
import {
	getPaginatedMediaLibraryData,
	filterMediaByFolder,
} from './utils/helpers'
import ModalUploadMedia from '../modals/ModalUploadMedia'
import MediaLibraryHeader from './MediaLibraryHeader'
import LayoutRow from '../layout/LayoutRow'
import MediaLibrarySearchFilter from './MediaLibrarySearchFilter'
import MediaLibrarySortFilter from './MediaLibrarySortFilter'
import MediaLibraryFolderNavigation from './MediaLibraryFolderNavigation'
import MediaGridPreviewModal from './MediaLibraryPreviewModal'
import { ROWS_PER_PAGE_OPTIONS } from './utils/constants'
import { type MediaFileProps } from './types'
import { MediaLibraryContentProps } from './MediaLibrary'

const MediaLibraryContent = ({
	search,
	pagination,
}: MediaLibraryContentProps) => {
	const { formatMessage } = useIntl()

	const {
		data,
		folders,
		loading,
		isDragging,
		selectedFolder,
		selectedFiles,
		bulkSelect,
		bulkSelectActions,
		uploadModalVisible,
		getRootProps,
		getInputProps,
		handleCancelUploadModal,
		handleUploadSubmit,
		handleSelectFile,
		handleCloseMediaPreview,
	} = useMediaLibrary()

	const pageNumber = pagination?.pageNumber ?? 1
	const pageSize = pagination?.pageSize ?? 10

	const handleRowsPerPageChange = (event: React.ChangeEvent) => {
		const newPageSize = parseInt(
			(event.target as HTMLInputElement).value,
			10
		)
		pagination?.handleRowsPerPageChange(newPageSize, pageNumber)
	}

	const handlePageChange = (
		event: React.MouseEvent | null,
		page: number
	) => {
		const newPageNumber = page + 1
		pagination?.handlePageChange(newPageNumber)
	}

	const selectedData = React.useMemo(() => {
		return selectedFolder
			? filterMediaByFolder(data, selectedFolder)
			: data
	}, [data, selectedFolder])

	const paginatedData = React.useMemo(() => {
		return pagination
			? getPaginatedMediaLibraryData(
					selectedData,
					pageNumber,
					pageSize
				)
			: selectedData
	}, [pageNumber, pageSize, pagination, selectedData])

	return (
		<Box sx={{ width: '100%' }}>
			<ModalUploadMedia
				open={uploadModalVisible}
				onSubmit={handleUploadSubmit}
				onCancel={handleCancelUploadModal}
			/>

			<MediaLibraryHeader />

			{(search || pagination?.handleSort) && (
				<LayoutRow className="flex flex-col md:flex-row md:justify-end gap-6 sm:gap-10 sm:items-start">
					{search && (
						<MediaLibrarySearchFilter
							searchText={search.searchText}
							onSearchChange={search.handleSearchChange}
							onClearSearchClick={search.handleClearSearchClick}
						/>
					)}
					{pagination?.handleSort && (
						<MediaLibrarySortFilter
							sortBy={pagination?.sortBy as keyof MediaFileProps}
							sortDirection={pagination?.sortDirection}
							onSort={pagination?.handleSort}
						/>
					)}
				</LayoutRow>
			)}
			<div className="md:flex w-full">
				<div className="sm:max-w-60 lg:max-w-72 xl:max-w-80 w-full flex-shrink-0 border-gray-200 dark:border-gray-700 md:pr-6">
					{folders && <MediaLibraryFolderNavigation />}
				</div>

				<div className="w-full flex flex-col" {...getRootProps()}>
					<input {...getInputProps()} />
					<div className="relative h-full md:min-h-[620px] bg-slate-50 dark:bg-neutral-800 mb-6 p-4">
						<ul
							className={clsx(
								'relative w-full grid grid-cols-2 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 transition-opacity duration-300',
								isDragging && 'opacity-20'
							)}
							data-cy="media-library-list"
						>
							{paginatedData?.map((media) => (
								<li key={media.id} className="col-span-1 w-full">
									<MediaLibraryCard
										media={media}
										bulkSelect={!!bulkSelectActions && bulkSelect}
										selected={selectedFiles.some(
											(selectedFile) => selectedFile.id === media.id
										)}
										onSelect={handleSelectFile}
										loading={loading}
									/>
								</li>
							))}
						</ul>
					</div>
				</div>
				<MediaGridPreviewModal
					media={selectedFiles[0]}
					open={selectedFiles.length > 0 && !bulkSelect}
					onClose={handleCloseMediaPreview}
				/>
			</div>
			{pagination && (
				<TablePagination
					rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
					component="div"
					count={selectedData.length}
					rowsPerPage={pageSize}
					page={pageNumber - 1}
					onPageChange={handlePageChange}
					onRowsPerPageChange={handleRowsPerPageChange}
					labelRowsPerPage={formatMessage({
						id: 'media_library.rows_per_page_label',
					})}
				/>
			)}
		</Box>
	)
}

export default MediaLibraryContent
